import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { Badge, ErrorMsg, Loading, PageHead, Section, Time } from '../../components/ui';
import { api, errorText, newIdempotencyKey } from '../../lib/api';
import {
  ATTENTION_FLAGS,
  CONDITION_LABEL,
  EVIDENCE_LABEL,
  FIELD_LABEL,
  FLAG_LABEL,
  money,
  PAGE_METHOD_LABEL,
  type BBox,
  type EvidenceType,
  type QuotationDetail,
  type QuoteCondition,
  type QuoteField,
  type QuoteLine,
  type ReviewState,
} from '../../lib/pricingTypes';
import { useApi, useSession } from '../../lib/session';
import { QuoteStatusMark } from './Pricing';
import './pricing.css';

type Sel = { kind: 'field' | 'line' | 'condition'; id: string; page: number | null; bbox: BBox | null } | null;
type ReviewBody = {
  fields?: { key: string; value?: string | null; state: Exclude<ReviewState, 'proposed'> }[];
  lines?: Record<string, unknown>[];
  conditions?: Record<string, unknown>[];
  evidence_type?: EvidenceType;
};

const STATE_LABEL: Record<ReviewState, string> = {
  proposed: 'proposed',
  accepted: 'accepted',
  corrected: 'corrected',
  excluded: 'excluded',
  unknown: 'unknown',
};
const STATE_TONE: Record<ReviewState, string> = { proposed: 'warn', accepted: 'ok', corrected: 'ok', excluded: 'idle', unknown: 'idle' };
const DATE_KEYS = new Set(['issue_date', 'expiry_date']);
const MONEY_KEYS = new Set(['subtotal', 'discount', 'net', 'tax', 'total']);

function Flags({ flags }: { flags: string[] }) {
  if (!flags.length) return null;
  return (
    <span className="flags">
      {flags.map((f) => (
        <Badge key={f} tone={ATTENTION_FLAGS.has(f) ? 'bad' : undefined} title={f}>
          {FLAG_LABEL[f] ?? f.replace(/_/g, ' ')}
        </Badge>
      ))}
    </span>
  );
}

function StateBadge({ s }: { s: ReviewState }) {
  return <Badge tone={STATE_TONE[s]}>{STATE_LABEL[s]}</Badge>;
}

export function QuotationReview() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const { data: q, error, loading, reload, setData } = useApi<QuotationDetail>(`/api/quotations/${id}`);
  const { settings } = useSession();
  const [sel, setSel] = useState<Sel>(null);
  const [busy, setBusy] = useState(false);
  const [actionErr, setActionErr] = useState('');
  const [notice, setNotice] = useState('');
  const viewer = useRef<HTMLDivElement>(null);

  // Poll while the worker extracts.
  const running = q && (q.status === 'extracting' || (q.job && ['queued', 'running'].includes(q.job.status)));
  useEffect(() => {
    if (!running) return;
    const t = window.setInterval(reload, 2000);
    return () => window.clearInterval(t);
  }, [running, reload]);

  // Deep link from Comparisons: ?line=<id> selects that line once it is loaded.
  const lineParam = sp.get('line');
  useEffect(() => {
    if (!q || !lineParam) return;
    const ln = q.lines.find((l) => l.id === lineParam);
    if (ln) setSel({ kind: 'line', id: ln.id, page: ln.page, bbox: ln.bbox });
  }, [q?.id, lineParam]);

  // Scroll the viewer to the selected page.
  useEffect(() => {
    if (!sel?.page || !viewer.current) return;
    const el = viewer.current.querySelector<HTMLElement>(`[data-page="${sel.page}"]`);
    const box = el?.querySelector<HTMLElement>('.hl.active');
    (box ?? el)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [sel]);

  const categories = useMemo(() => {
    const base = settings?.values.service_categories ?? [];
    return [...new Set([...base, 'Bundle', 'Hosting', 'Modelling', 'Revisions'])];
  }, [settings]);

  if (loading && !q) return <Loading what="Loading the quotation" />;
  if (!q) return <ErrorMsg>{error || 'The quotation could not be loaded.'}</ErrorMsg>;

  async function save(body: ReviewBody, msg?: string) {
    if (!q) return;
    setBusy(true);
    setActionErr('');
    setNotice('');
    try {
      const r = await api<{ quotation: QuotationDetail; approval_revoked: boolean }>(`/api/quotations/${q.id}/review`, {
        method: 'PATCH',
        body: { version: q.version, ...body },
      });
      setData(r.quotation);
      setNotice(r.approval_revoked ? 'Saved. The approval was revoked: approve again once the review is complete.' : msg ?? 'Saved.');
    } catch (e) {
      setActionErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function act(path: string, body?: unknown, msg?: string) {
    if (!q) return;
    setBusy(true);
    setActionErr('');
    setNotice('');
    try {
      const r = await api<QuotationDetail>(`/api/quotations/${q.id}/${path}`, { method: 'POST', body: body ?? {} });
      setData(r);
      if (msg) setNotice(msg);
    } catch (e) {
      setActionErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function applyVersion(version: number) {
    if (!q) return;
    setBusy(true);
    setActionErr('');
    try {
      await api(`/api/quotations/${q.id}/extractions/${version}/apply`, { method: 'POST', body: {} });
      setNotice(`Extraction v${version} applied to unreviewed values; reviewed values were kept.`);
      reload();
    } catch (e) {
      setActionErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  async function rerun() {
    if (!q) return;
    setBusy(true);
    setActionErr('');
    try {
      await api(`/api/quotations/${q.id}/extract`, { method: 'POST', idempotencyKey: newIdempotencyKey() });
      setNotice(
        q.status === 'approved'
          ? 'Extraction queued. The quotation is approved, so the new run is kept as a proposal and changes nothing.'
          : 'Extraction queued. Values you reviewed are kept; only unreviewed ones are replaced.',
      );
      reload();
    } catch (e) {
      setActionErr(errorText(e));
    } finally {
      setBusy(false);
    }
  }

  const pending = q.pending?.total ?? 0;
  const attention = (flags: string[]) => flags.some((f) => ATTENTION_FLAGS.has(f));
  const bulk: ReviewBody = {
    fields: q.fields.filter((f) => f.review_state === 'proposed' && f.value !== null && !attention(f.flags)).map((f) => ({ key: f.key, state: 'accepted' as const })),
    lines: q.lines.filter((l) => l.review_state === 'proposed' && l.line_total !== null && !attention(l.flags)).map((l) => ({ id: l.id, state: 'accepted' })),
    conditions: q.conditions.filter((c) => c.review_state === 'proposed').map((c) => ({ id: c.id, state: 'accepted' })),
  };
  const bulkCount = (bulk.fields?.length ?? 0) + (bulk.lines?.length ?? 0) + (bulk.conditions?.length ?? 0);
  const canEdit = !['extracting', 'password_protected', 'malformed'].includes(q.status);
  const allBoxes: { id: string; page: number | null; bbox: BBox | null }[] = [
    ...q.fields.map((f) => ({ id: f.id, page: f.page, bbox: f.bbox })),
    ...q.lines.map((l) => ({ id: l.id, page: l.page, bbox: l.bbox })),
    ...q.conditions.map((c) => ({ id: c.id, page: c.page, bbox: c.bbox })),
  ];
  const latest = q.extractions[0];

  return (
    <div className="page wide">
      <p className="crumbs">
        <Link to="/pricing">Pricing</Link> / quotation
      </p>
      <PageHead title={q.filename} meta={<QuoteStatusMark status={q.status} />}>
        <a className="btn small" href={`/api/quotations/${q.id}/file`}>
          Download original
        </a>
        {canEdit && (
          <button type="button" className="btn small" disabled={busy || !!running} onClick={() => void rerun()}>
            Rerun extraction
          </button>
        )}
        {q.status === 'approved' ? (
          <button type="button" className="btn small" disabled={busy} onClick={() => void act('reopen', undefined, 'Reopened for review. It is out of comparisons until approved again.')}>
            Reopen review
          </button>
        ) : (
          <button
            type="button"
            className="btn primary small"
            disabled={busy || q.status !== 'needs_review' || pending > 0}
            title={pending > 0 ? `${pending} value(s) still unreviewed` : undefined}
            onClick={() => void act('approve', { version: q.version }, 'Approved: its reviewed lines now count in comparisons.')}
          >
            Approve
          </button>
        )}
      </PageHead>

      {q.status_detail && <p className={`msg${['password_protected', 'malformed', 'failed'].includes(q.status) ? ' err' : ''}`}>{q.status_detail}</p>}
      {running && (
        <p className="msg" role="status">
          Extracting{q.job?.progress?.page ? `: page ${String(q.job.progress.page)} of ${String(q.job.progress.of)}` : ''}… this page refreshes by itself.
        </p>
      )}
      <ErrorMsg>{actionErr}</ErrorMsg>
      {notice && (
        <p className="msg ok" role="status">
          {notice}
        </p>
      )}
      {latest?.warnings.map((w) => (
        <p key={w} className="msg warn">
          {w}
        </p>
      ))}

      <div className="review-grid">
        <div className="viewer" ref={viewer} aria-label="Document pages">
          {q.pages.length === 0 && <p className="muted">No page has been read yet.</p>}
          {q.pages.map((p) => (
            <figure key={p.page} className="pdf-page" data-page={p.page}>
              <figcaption className="small">
                Page {p.page} · {PAGE_METHOD_LABEL[p.method] ?? p.method}
                {p.ocr_confidence != null && ` (mean confidence ${p.ocr_confidence})`}
                {p.note && <span className="err-text"> · {p.note}</span>}
              </figcaption>
              <div className="page-img" style={{ aspectRatio: `${p.width_pt} / ${p.height_pt}` }}>
                <img src={`/api/quotations/${q.id}/pages/${p.page}/image?w=900`} alt={`Page ${p.page} of ${q.filename}`} loading="lazy" />
                {allBoxes
                  .filter((b) => b.page === p.page && b.bbox)
                  .map((b) => {
                    const [x0, y0, x1, y1] = b.bbox as BBox;
                    const active = sel?.id === b.id;
                    return (
                      <span
                        key={b.id}
                        className={`hl${active ? ' active' : ''}`}
                        style={{
                          left: `${(x0 / p.width_pt) * 100}%`,
                          top: `${(y0 / p.height_pt) * 100}%`,
                          width: `${((x1 - x0) / p.width_pt) * 100}%`,
                          height: `${((y1 - y0) / p.height_pt) * 100}%`,
                        }}
                      />
                    );
                  })}
              </div>
            </figure>
          ))}
        </div>

        <div className="review-panel">
          <Section
            title="Review"
            aside={
              <span className="small">
                {pending > 0 ? (
                  <>
                    <strong>{pending}</strong> unreviewed
                  </>
                ) : (
                  'all values reviewed'
                )}
              </span>
            }
          >
            <div className="row gap wrap">
              <label className="select-filter">
                <span>Evidence type</span>
                <select disabled={!canEdit || busy} value={q.evidence_type} onChange={(e) => void save({ evidence_type: e.target.value as EvidenceType })}>
                  {Object.entries(EVIDENCE_LABEL).map(([k, l]) => (
                    <option key={k} value={k}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
              {canEdit && bulkCount > 0 && (
                <button type="button" className="btn small" disabled={busy} onClick={() => void save(bulk, `${bulkCount} unflagged value(s) accepted as printed.`)}>
                  Accept {bulkCount} unflagged as printed
                </button>
              )}
            </div>
            <p className="small muted">
              Flagged values (red) and values without an amount are never bulk-accepted: open each one. Select a row to see where it is printed.
            </p>
          </Section>

          <Checks q={q} onSelect={(lineId) => {
            const ln = q.lines.find((l) => l.id === lineId);
            if (ln) setSel({ kind: 'line', id: ln.id, page: ln.page, bbox: ln.bbox });
          }} />

          <Section title="Document">
            <div className="table-wrap">
              <table className="table compact review-table">
                <thead>
                  <tr>
                    <th>Field</th>
                    <th>Value</th>
                    <th>Printed</th>
                    <th>State</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {q.fields.map((f) => (
                    <FieldRow key={f.id} f={f} selected={sel?.id === f.id} disabled={!canEdit || busy} onSelect={() => setSel({ kind: 'field', id: f.id, page: f.page, bbox: f.bbox })} save={save} />
                  ))}
                  <AddField q={q} disabled={!canEdit || busy} save={save} />
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="Lines" aside={<span className="small muted">optional items stay out of base totals; bundles are never split</span>}>
            <div className="table-wrap">
              <table className="table compact review-table lines-table">
                <thead>
                  <tr>
                    <th>Line as printed</th>
                    <th>Category</th>
                    <th className="num">Qty</th>
                    <th className="num">Unit price</th>
                    <th className="num">Total</th>
                    <th>Kind</th>
                    <th>State</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {q.lines.map((l) => (
                    <LineRow
                      key={l.id}
                      l={l}
                      categories={categories}
                      selected={sel?.id === l.id}
                      disabled={!canEdit || busy}
                      onSelect={() => setSel({ kind: 'line', id: l.id, page: l.page, bbox: l.bbox })}
                      save={save}
                    />
                  ))}
                  {q.lines.length === 0 && (
                    <tr>
                      <td colSpan={8} className="muted">
                        No priced line was recognised.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {canEdit && <AddLine categories={categories} currency={q.currency} disabled={busy} save={save} />}
          </Section>

          <Section title="Conditions">
            {q.conditions.length === 0 && <p className="muted small">No condition recognised.</p>}
            <ul className="conditions">
              {q.conditions.map((c) => (
                <ConditionRow key={c.id} c={c} kinds={q.condition_kinds} selected={sel?.id === c.id} disabled={!canEdit || busy} onSelect={() => setSel({ kind: 'condition', id: c.id, page: c.page, bbox: c.bbox })} save={save} />
              ))}
            </ul>
          </Section>

          <Section title="Extraction runs">
            <ul className="lines">
              {q.extractions.map((e) => {
                const applied = e.applied as { applied?: boolean; reason?: string; fields_kept?: number; lines_kept?: number } | null;
                return (
                  <li key={e.version}>
                    <span className="num">v{e.version}</span>
                    <span>
                      <Time iso={e.created_at} /> · {e.extractor} · {e.lines} lines ·{' '}
                      {applied?.applied === false ? (
                        <>
                          <span className="muted">kept as a proposal ({applied.reason})</span>
                          {q.status === 'needs_review' && (
                            <>
                              {' '}
                              <button type="button" className="link" disabled={busy} onClick={() => void applyVersion(e.version)}>
                                apply to unreviewed values
                              </button>
                            </>
                          )}
                        </>
                      ) : (
                        <span className="muted">
                          applied
                          {applied && (applied.fields_kept || applied.lines_kept) ? `; kept ${applied.fields_kept ?? 0} reviewed field(s), ${applied.lines_kept ?? 0} line(s)` : ''}
                        </span>
                      )}
                      {e.job_id && (
                        <>
                          {' · '}
                          <Link to={`/jobs/${e.job_id}`}>job</Link>
                        </>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
            <p className="small muted">
              SHA-256 {q.sha256.slice(0, 16)}… · {(q.bytes / 1024).toFixed(0)} KB · uploaded <Time iso={q.created_at} />. Extraction is rule-based (no model is wired):
              confidence is advisory.
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Checks({ q, onSelect }: { q: QuotationDetail; onSelect: (lineId: string) => void }) {
  const r = q.reconciliation;
  return (
    <Section
      title="Arithmetic"
      aside={<Badge tone={r.status === 'consistent' ? 'ok' : r.status === 'inconsistent' ? 'bad' : 'warn'}>{r.status}</Badge>}
    >
      <p className="small muted">Checked in decimal on the current values. Nothing is adjusted to make totals fit.</p>
      <ul className="checks">
        {r.checks.map((c, i) => (
          <li key={i} className={`check-${c.status}`}>
            <span className="check-mark" aria-hidden="true">
              {c.status === 'ok' ? '✓' : c.status === 'mismatch' ? '✗' : '·'}
            </span>
            <span>
              {c.line_id ? (
                <button type="button" className="link" onClick={() => onSelect(c.line_id!)}>
                  {c.label}
                </button>
              ) : (
                c.label
              )}
              {c.status === 'mismatch' && (
                <span className="err-text">
                  {' '}
                  — expected {money(c.expected)}, printed {money(c.found)} (difference {money(c.difference)})
                </span>
              )}
              {c.status === 'not_checkable' && <span className="muted"> — not checkable{c.note ? `: ${c.note}` : ''}</span>}
              {c.status !== 'not_checkable' && c.note && <span className="muted"> — {c.note}</span>}
            </span>
          </li>
        ))}
      </ul>
      {r.optional_sum && <p className="small muted">Optional items total {money(r.optional_sum)} (outside the base total).</p>}
    </Section>
  );
}

function Actions({
  state,
  hasValue,
  dirty,
  disabled,
  onState,
  onSave,
}: {
  state: ReviewState;
  hasValue: boolean;
  dirty: boolean;
  disabled: boolean;
  onState: (s: Exclude<ReviewState, 'proposed' | 'corrected'>) => void;
  onSave: () => void;
}) {
  return (
    <span className="row-actions">
      {dirty ? (
        <button type="button" className="btn small primary" disabled={disabled} onClick={onSave}>
          Save
        </button>
      ) : (
        state === 'proposed' &&
        hasValue && (
          <button type="button" className="btn small" disabled={disabled} onClick={() => onState('accepted')}>
            Accept
          </button>
        )
      )}
      {state !== 'unknown' && (
        <button type="button" className="btn small ghost" disabled={disabled} onClick={() => onState('unknown')} title="The document does not say, or it cannot be read">
          Unknown
        </button>
      )}
      {state !== 'excluded' && (
        <button type="button" className="btn small ghost" disabled={disabled} onClick={() => onState('excluded')} title="Keep as evidence but leave out of everything">
          Exclude
        </button>
      )}
    </span>
  );
}

function FieldRow({
  f,
  selected,
  disabled,
  onSelect,
  save,
}: {
  f: QuoteField;
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
  save: (b: ReviewBody, msg?: string) => Promise<void>;
}) {
  const [v, setV] = useState(f.value ?? '');
  useEffect(() => setV(f.value ?? ''), [f.value]);
  const dirty = v !== (f.value ?? '') && f.key !== 'supplier_studio';
  const label = FIELD_LABEL[f.key] ?? f.key;
  return (
    <tr className={selected ? 'selected' : f.review_state === 'excluded' ? 'dim' : undefined} onClick={onSelect}>
      <th scope="row" className="field-key">
        {label}
        {f.page && (
          <button type="button" className="link small page-link" onClick={onSelect}>
            p. {f.page}
          </button>
        )}
      </th>
      <td>
        {f.key === 'supplier_studio' ? (
          f.value ? (
            <Link to={`/competitors/${f.value}`}>{f.label ?? 'studio'}</Link>
          ) : (
            <span className="unknown">none</span>
          )
        ) : f.key === 'tax_treatment' ? (
          <select aria-label={label} value={v} disabled={disabled} onChange={(e) => setV(e.target.value)}>
            <option value="">unknown</option>
            <option value="excl_tax">prices excl. tax</option>
            <option value="incl_tax">prices incl. tax</option>
            <option value="no_tax">no tax (exempt / reverse charge)</option>
            <option value="unknown">unknown</option>
          </select>
        ) : (
          <input
            aria-label={label}
            className={MONEY_KEYS.has(f.key) ? 'num' : undefined}
            type={DATE_KEYS.has(f.key) ? 'date' : 'text'}
            value={v}
            disabled={disabled}
            placeholder="unknown"
            onChange={(e) => setV(e.target.value)}
          />
        )}
        <Flags flags={f.flags} />
        {f.newer_proposal && <div className="small muted">a newer run reads: {f.newer_proposal}</div>}
      </td>
      <td className="small muted clip">{f.original ?? '—'}</td>
      <td>
        <StateBadge s={f.review_state} />
      </td>
      <td>
        <Actions
          state={f.review_state}
          hasValue={f.value !== null}
          dirty={dirty}
          disabled={disabled}
          onState={(s) => void save({ fields: [{ key: f.key, state: s }] })}
          onSave={() => void save({ fields: [{ key: f.key, value: v || null, state: v ? 'corrected' : 'unknown' }] })}
        />
      </td>
    </tr>
  );
}

function AddField({ q, disabled, save }: { q: QuotationDetail; disabled: boolean; save: (b: ReviewBody, msg?: string) => Promise<void> }) {
  const missing = q.field_keys.filter((k) => k !== 'supplier_studio' && !q.fields.some((f) => f.key === k));
  const [key, setKey] = useState('');
  const [v, setV] = useState('');
  if (!missing.length) return null;
  return (
    <tr>
      <td>
        <select aria-label="Add a field" value={key} disabled={disabled} onChange={(e) => setKey(e.target.value)}>
          <option value="">Add a field…</option>
          {missing.map((k) => (
            <option key={k} value={k}>
              {FIELD_LABEL[k] ?? k}
            </option>
          ))}
        </select>
      </td>
      <td colSpan={3}>
        {key && <input aria-label="Value" type={DATE_KEYS.has(key) ? 'date' : 'text'} value={v} disabled={disabled} onChange={(e) => setV(e.target.value)} />}
      </td>
      <td>
        {key && (
          <button
            type="button"
            className="btn small"
            disabled={disabled || !v}
            onClick={() =>
              void save({ fields: [{ key, value: v, state: 'corrected' }] }).then(() => {
                setKey('');
                setV('');
              })
            }
          >
            Add
          </button>
        )}
      </td>
    </tr>
  );
}

const LINE_EDIT = ['label_original', 'category', 'quantity', 'unit_price', 'line_total', 'currency', 'is_optional', 'is_bundle', 'scope_notes'] as const;
type LineDraft = Pick<QuoteLine, (typeof LINE_EDIT)[number]>;
const draftOf = (l: QuoteLine): LineDraft => ({
  label_original: l.label_original,
  category: l.category,
  quantity: l.quantity,
  unit_price: l.unit_price,
  line_total: l.line_total,
  currency: l.currency,
  is_optional: l.is_optional,
  is_bundle: l.is_bundle,
  scope_notes: l.scope_notes,
});
const norm = (v: string | null) => (v === '' ? null : v);

function LineRow({
  l,
  categories,
  selected,
  disabled,
  onSelect,
  save,
}: {
  l: QuoteLine;
  categories: string[];
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
  save: (b: ReviewBody, msg?: string) => Promise<void>;
}) {
  const [d, setD] = useState<LineDraft>(draftOf(l));
  useEffect(() => setD(draftOf(l)), [l]);
  const changed = LINE_EDIT.filter((k) => {
    const a = d[k];
    const b = l[k];
    return typeof a === 'boolean' ? a !== b : norm((a as string | null) ?? null) !== (b ?? null);
  });
  const dirty = changed.length > 0;
  const set = <K extends keyof LineDraft>(k: K, v: LineDraft[K]) => setD({ ...d, [k]: v });
  const patch = Object.fromEntries(changed.map((k) => [k, typeof d[k] === 'string' ? norm(d[k] as string) : d[k]]));
  return (
    <tr className={selected ? 'selected' : l.review_state === 'excluded' || l.review_state === 'unknown' ? 'dim' : undefined} onClick={onSelect}>
      <td className="line-label">
        <input aria-label="Line label" value={d.label_original} disabled={disabled} onChange={(e) => set('label_original', e.target.value)} />
        <div className="small muted">
          {l.page ? `p. ${l.page}` : l.method === 'manual' ? 'added by hand' : ''}
          {l.original.line_total ? ` · printed ${String(l.original.line_total)}` : ''}
          {l.duration ? ` · ${l.duration}` : ''}
          {l.resolution ? ` · ${l.resolution}` : ''}
          {l.is_bundle && l.bundle_components.length > 0 ? ` · bundle: ${l.bundle_components.join(' + ')}` : ''}
        </div>
        <Flags flags={l.flags} />
      </td>
      <td>
        <select aria-label="Category" value={d.category ?? ''} disabled={disabled} onChange={(e) => set('category', e.target.value || null)}>
          <option value="">uncategorised</option>
          {[...new Set([...(d.category ? [d.category] : []), ...categories])].map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </td>
      <td>
        <input aria-label="Quantity" className="num qty" value={d.quantity ?? ''} placeholder="—" disabled={disabled} onChange={(e) => set('quantity', e.target.value)} />
      </td>
      <td>
        <input aria-label="Unit price" className="num amt" value={d.unit_price ?? ''} placeholder="—" disabled={disabled} onChange={(e) => set('unit_price', e.target.value)} />
      </td>
      <td>
        <input aria-label="Line total" className="num amt" value={d.line_total ?? ''} placeholder="unknown" disabled={disabled} onChange={(e) => set('line_total', e.target.value)} />
        <input aria-label="Currency" className="cur" value={d.currency ?? ''} placeholder="CUR" maxLength={3} disabled={disabled} onChange={(e) => set('currency', e.target.value.toUpperCase())} />
      </td>
      <td className="small">
        <label className="check">
          <input type="checkbox" checked={d.is_optional} disabled={disabled} onChange={(e) => set('is_optional', e.target.checked)} /> optional
        </label>
        <label className="check">
          <input type="checkbox" checked={d.is_bundle} disabled={disabled} onChange={(e) => set('is_bundle', e.target.checked)} /> bundle
        </label>
      </td>
      <td>
        <StateBadge s={l.review_state} />
      </td>
      <td>
        <Actions
          state={l.review_state}
          hasValue={l.line_total !== null}
          dirty={dirty}
          disabled={disabled}
          onState={(s) => void save({ lines: [{ id: l.id, state: s }] })}
          onSave={() => void save({ lines: [{ id: l.id, ...patch, state: 'corrected' }] })}
        />
        {l.method === 'manual' && (
          <button type="button" className="btn small ghost" disabled={disabled} onClick={() => void save({ lines: [{ id: l.id, delete: true }] }, 'Line removed.')}>
            Delete
          </button>
        )}
      </td>
    </tr>
  );
}

function AddLine({ categories, currency, disabled, save }: { categories: string[]; currency: string | null; disabled: boolean; save: (b: ReviewBody, msg?: string) => Promise<void> }) {
  const empty = { label_original: '', category: '', quantity: '', unit_price: '', line_total: '', currency: currency ?? '' };
  const [d, setD] = useState(empty);
  const [open, setOpen] = useState(false);
  if (!open)
    return (
      <p>
        <button type="button" className="btn small" disabled={disabled} onClick={() => setOpen(true)}>
          Add a line by hand
        </button>
      </p>
    );
  return (
    <form
      className="inline-form add-line"
      aria-label="Add a line"
      onSubmit={(e) => {
        e.preventDefault();
        void save(
          {
            lines: [
              {
                label_original: d.label_original,
                category: d.category || null,
                quantity: d.quantity || null,
                unit_price: d.unit_price || null,
                line_total: d.line_total || null,
                currency: d.currency || null,
              },
            ],
          },
          'Line added.',
        ).then(() => {
          setD(empty);
          setOpen(false);
        });
      }}
    >
      <input aria-label="New line label" placeholder="Label" required value={d.label_original} onChange={(e) => setD({ ...d, label_original: e.target.value })} />
      <select aria-label="New line category" value={d.category} onChange={(e) => setD({ ...d, category: e.target.value })}>
        <option value="">uncategorised</option>
        {categories.map((c) => (
          <option key={c}>{c}</option>
        ))}
      </select>
      <input aria-label="New line quantity" className="num qty" placeholder="Qty" value={d.quantity} onChange={(e) => setD({ ...d, quantity: e.target.value })} />
      <input aria-label="New line total" className="num amt" placeholder="Total" value={d.line_total} onChange={(e) => setD({ ...d, line_total: e.target.value })} />
      <input aria-label="New line currency" className="cur" placeholder="CUR" maxLength={3} value={d.currency} onChange={(e) => setD({ ...d, currency: e.target.value.toUpperCase() })} />
      <button type="submit" className="btn small primary" disabled={disabled || !d.label_original}>
        Add line
      </button>
      <button type="button" className="btn small ghost" onClick={() => setOpen(false)}>
        Cancel
      </button>
    </form>
  );
}

function ConditionRow({
  c,
  kinds,
  selected,
  disabled,
  onSelect,
  save,
}: {
  c: QuoteCondition;
  kinds: string[];
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
  save: (b: ReviewBody, msg?: string) => Promise<void>;
}) {
  const [kind, setKind] = useState(c.kind);
  const [text, setText] = useState(c.text);
  useEffect(() => {
    setKind(c.kind);
    setText(c.text);
  }, [c.kind, c.text]);
  const dirty = kind !== c.kind || text !== c.text;
  return (
    <li className={`condition${selected ? ' selected' : ''}${c.review_state === 'excluded' ? ' dim' : ''}`} onClick={onSelect}>
      <div className="row gap wrap">
        <select aria-label="Condition kind" value={kind} disabled={disabled} onChange={(e) => setKind(e.target.value)}>
          {kinds.map((k) => (
            <option key={k} value={k}>
              {CONDITION_LABEL[k] ?? k}
            </option>
          ))}
        </select>
        <StateBadge s={c.review_state} />
        {c.page && <span className="small muted">p. {c.page}</span>}
        {c.value && <span className="small muted">{Object.entries(c.value).map(([k, v]) => `${k} ${v}`).join(' · ')}</span>}
      </div>
      <textarea aria-label="Condition text" rows={2} value={text} disabled={disabled} onChange={(e) => setText(e.target.value)} />
      <Actions
        state={c.review_state}
        hasValue
        dirty={dirty}
        disabled={disabled}
        onState={(s) => void save({ conditions: [{ id: c.id, state: s }] })}
        onSave={() => void save({ conditions: [{ id: c.id, kind, text, state: 'corrected' }] })}
      />
    </li>
  );
}
